import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../contexts/CartContext';
import useMediaQuery from '../hooks/useMediaQuery';
import { Product } from '../types';

interface FixedAddToCartButtonProps {
  product: Product;
  originalButtonRef: React.RefObject<HTMLDivElement | null>;
}

const FixedAddToCartButton: React.FC<FixedAddToCartButtonProps> = ({ product, originalButtonRef }) => {
  const { addToCart, toggleCart } = useCart();
  const [isVisible, setIsVisible] = useState(false);
  const isMobile = useMediaQuery('(max-width: 640px)');
  const [isAdding, setIsAdding] = useState(false);

  // Extract ASIN from Amazon link
  const extractAsin = (amazonLink: string): string => {
    // Try to extract ASIN from URL
    const asinMatch = amazonLink.match(/\/([A-Z0-9]{10})(?:\/|\?|$)/);
    return asinMatch ? asinMatch[1] : 'unknown';
  };

  const handleAddToCart = () => {
    setIsAdding(true);
    
    // Add item to cart
    addToCart({
      id: String(product.id),
      asin: extractAsin(product.amazonLink),
      name: product.name,
      image: product.image,
      quantity: 1
    });
    
    // Briefly show loading state then open cart
    setTimeout(() => {
      setIsAdding(false);
      toggleCart(true);
    }, 150);
  };

  useEffect(() => {
    const checkButtonVisibility = () => {
      if (!originalButtonRef?.current) return;
      
      const rect = originalButtonRef.current.getBoundingClientRect();
      // Check if the original button is out of viewport
      setIsVisible(rect.top < 0 || rect.bottom > window.innerHeight);
    };

    // Initial check
    checkButtonVisibility();
    
    // Add scroll event listener
    window.addEventListener('scroll', checkButtonVisibility);
    
    // Clean up
    return () => {
      window.removeEventListener('scroll', checkButtonVisibility);
    };
  }, [originalButtonRef]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.2 }}
          className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 py-3 px-4 shadow-lg z-40"
        >
          <div className="container-custom mx-auto">
            <div className={`flex items-center ${isMobile ? 'justify-between' : 'justify-between'}`}>
              {/* Product Info - Conditionally show based on screen size */}
              {!isMobile && (
                <div className="flex items-center flex-1 mr-4">
                  <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-md border border-gray-200 mr-4">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="h-full w-full object-cover object-center"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = `https://placehold.co/200x200@3x?text=${product.name.replace(/ /g, '+')}&font=poppins`;
                      }}
                    />
                  </div>
                  <div>
                    <h3 className="text-base font-display font-medium text-gray-900 truncate max-w-xs">
                      {product.name}
                    </h3>
                    <span className="text-lg font-bold text-brand-green-600">
                      ${product.price.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Mobile Price */}
              {isMobile && (
                <div className="flex items-center">
                  <span className="text-lg font-bold text-brand-green-600">
                    ${product.price.toFixed(2)}
                  </span>
                </div>
              )}
              
              {/* Add to Cart Button */}
              <div className={isMobile ? 'flex-1 ml-4' : ''}>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleAddToCart}
                  disabled={isAdding}
                  className={`btn btn-secondary flex items-center justify-center font-medium ${isMobile ? 'w-full' : ''} ${isAdding ? 'opacity-75 cursor-not-allowed' : 'shadow-md hover:shadow-lg transition-shadow'}`}
                >
                  {isAdding ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Adding...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      Add to Cart
                    </>
                  )}
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FixedAddToCartButton;
